import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import InformationIcon from '@material-ui/icons/Info';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import Button from '@material-ui/core/Button';
import FormLabel from '@material-ui/core/FormLabel';
import isEqual from 'lodash/isEqual';
import uuid from "uuid/v4";

const columnData = [
    { id: 'firstAttribute', numeric: false, disablePadding: false, label: '1st Attribute' },
    { id: 'secondAttribute', numeric: false, disablePadding: false, label: '2nd Attribute' },
    { id: 'thirdAttribute', numeric: false, disablePadding: false, label: '3rd Attribute' },
    { id: 'fourthAttribute', numeric: false, disablePadding: false, label: '4th Attribute' },
    { id: 'fifthAttribute', numeric: false, disablePadding: false, label: '5th Attribute' },
    { id: 'cumulative', numeric: false, disablePadding: false, label: 'Filter by all' }
];

class EnhancedTableHead extends React.Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    checkIfAllAreCumulative = (data) => {
        return !data.find(dim => dim.cumulative === false);
    }

    render() {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount, editMode, onSelectAllCulumative, cumSelected, data, classes } = this.props;
        return (
            <TableHead>
                <TableRow>
                    <TableCell style={{ padding: 1 }}>
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={numSelected === rowCount}
                            onChange={onSelectAllClick}
                            disabled={editMode}
                        />
                    </TableCell>
                    {columnData.map(column => {
                        if (column.id === "cumulative") {
                            return (
                                <TableCell
                                    key={column.id}
                                    numeric={column.numeric}
                                    padding={column.disablePadding ? 'none' : 'default'}
                                    style={{ minWidth: '184px', 'paddingLeft': 0 }}
                                >
                                    <Checkbox
                                        //indeterminate={numSelected > 0 && numSelected < rowCount}
                                        checked={this.checkIfAllAreCumulative(data)}
                                        onChange={onSelectAllCulumative}
                                        disabled={editMode}
                                        style={{ width: '30px', height: '30px', marginBottom: '3px' }}
                                    />
                                    <span className={classes.tableSpan}>
                                        {column.label}
                                    </span>

                                    <Tooltip PopperProps={{ style: { pointerEvents: 'none' } }} className={classes.tooltip} title="By selecting 'Filter by all' you can filter data by all values using '*' in your query screen or API.">
                                        <IconButton style={{ width: '30px', height: '30px', margin: '0 0 6px 0' }} size='small' aria-label="information">
                                            <InformationIcon style={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            );
                        } else {
                            return (
                                <TableCell
                                    key={column.id}
                                    numeric={column.numeric}
                                    padding={column.disablePadding ? 'none' : 'default'}
                                    sortDirection={orderBy === column.id ? order : false}
                                >
                                    <Tooltip
                                        title="Sort"
                                        placement={column.numeric ? 'bottom-end' : 'bottom-start'}
                                        enterDelay={300}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={order}
                                            onClick={this.createSortHandler(column.id)}
                                            style={{ fontSize: '11pt' }}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </Tooltip>
                                </TableCell>
                            );
                        }
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    classes: PropTypes.object,
    onSelectAllCulumative: PropTypes.func
};

const toolbarStyles = theme => ({
    root: {
        paddingRight: theme.spacing.unit,
    },
    highlight: theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
        },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
});

let EnhancedTableToolbar = props => {
    const { numSelected, classes } = props;

    return (
        <Toolbar
            className={classNames(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            <div className={classes.title}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subheading">
                        {numSelected} selected
                    </Typography>
                ) : (
                        <Typography variant="title" id="tableTitle">
                            Select All
                    </Typography>
                    )}
            </div>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
};

EnhancedTableToolbar = withStyles(toolbarStyles)(EnhancedTableToolbar);

const styles = theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing.unit * 3,
    },
    table: {
        minWidth: 100,
    },
    tableWrapper: {
        overflowX: 'auto',
        overflowY: 'hidden'
    },
    button: {
        'margin-right': theme.spacing.unit
    },
    buttonGroup: {
        display: 'flex',
        'flex-direction': 'row',
        'justify-content': 'flex-start'
    },
    headers: {
        fontSize: 22
    },
    tableSpan: {
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: 'inherit',
        justifyContent: 'flex-start',
        fontSize: '11pt',
        margin: '0 3px 0 20px'
    },
    tooltip: {
        fontSize: 16,
    }
});

class DimensionsTable extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            order: 'asc',
            orderBy: 'firstAttribute',
            selected: [],
            data: [].sort((a, b) => (a['dimensions']['firstAttribute'] < b['dimensions']['firstAttribute'] ? -1 : 1)),
            fieldsToAggregate: [],
            editMode: true,
            records: null,
            groupId: null,
            attributes: [],
        };
    }

    componentWillReceiveProps(nextProps) {
        let fieldsToAggregate = DimensionsTable.getFieldsToAggregate(nextProps.attributes);
        this.setState({
            fieldsToAggregate: fieldsToAggregate,
            editMode: nextProps.editDataGroupParams.editMode
        });
        if (Object.keys(nextProps.editDataGroupParams).length !== 0 &&
            (nextProps.attributes !== this.state.attributes ||
                nextProps.editDataGroupParams.id != this.state.groupId ||
                nextProps.editDataGroupParams.records != this.state.records)) {
            this.setState({ groupId: nextProps.editDataGroupParams.id, attributes: nextProps.attributes });
            this.setTableData(nextProps.editDataGroupParams, fieldsToAggregate);
        }
    }

    handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        const data = order === 'desc'
            ? this.state.data.sort((a, b) => (b['dimensions'][orderBy] < a['dimensions'][orderBy] ? -1 : 1))
            : this.state.data.sort((a, b) => (a['dimensions'][orderBy] < b['dimensions'][orderBy] ? -1 : 1));

        this.setState({ data, order, orderBy });
    };

    handleSelectAllClick = (event, checked) => {
        if (checked) {
            this.setState({ selected: this.state.data.map(dimension => dimension.aggID) });
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event, id) => {
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        this.setState({ selected: newSelected });
    };

    handleSelectAllCumulative = (event, checked) => {
        let { data } = this.state;
        this.setState({
            data: data.map(dim => {
                return dim = { ...dim, cumulative: checked };
            })
        }, () => this.sendDimensions());
    }

    isSelected = id => this.state.selected.indexOf(id) !== -1;

    handleAddRow = () => {
        if (this.state.data.length === this.props.dimensionsLimit) {
            this.props.clientLimitExceeded("Dimensions");
        } else {
            const newRow = {
                aggID: uuid(),
                dimensions: {
                    firstAttribute: "",
                    secondAttribute: "",
                    thirdAttribute: "",
                    fourthAttribute: "",
                    fifthAttribute: ""
                },
                cumulative: false
            };
            this.setState({
                data: [...this.state.data, newRow],
            });
        }
        this.sendDimensions();
    };

    handleDeleteRow = () => {
        const toDelete = this.state.selected;

        for (let i = this.state.data.length - 1; i > -1; i--) {
            let obj = this.state.data[i];
            if (toDelete.indexOf(obj.aggID) != -1) {
                this.state.data.splice(i, 1);
            }
        }
        this.state.selected = [];
        this.sendDimensions();
        this.forceUpdate();
    };

    handleFieldChange = (event) => {
        const { data } = this.state,
            { name, value, id } = event.target;
        this.setState({
            data: data.map(dim => {
                if (dim.aggID === id) {
                    let updatedDims = { ...dim.dimensions };
                    updatedDims[name] = value;
                    dim.dimensions = updatedDims;
                    return dim;
                }
                return dim;
            })
        }, () => this.sendDimensions());
    }

    handleCulumativeChange = (event) => {
        const { data } = this.state,
            { checked, id } = event.target;
        this.setState({ data: data.map(dim => dim.aggID === id ? { ...dim, cumulative: checked } : dim) }, () => this.sendDimensions());
    }

    static getFieldsToAggregate(attributes) {
        let fieldsToAggregate = [];
        for (let att in attributes) {
            fieldsToAggregate.push({
                value: attributes[att],
                name: attributes[att]
            })
        }
        return fieldsToAggregate;
    }

    sendDimensions = () => {
        let transformedDims = this.transformDimensions(this.state.data);
        this.props.dimensionsCallback({
            aggregationDimensions: transformedDims,
            unsavedChanges: this.checkForUnsavedChanges()
        });
    };

    checkForUnsavedChanges() {
        if (this.props.savedGroup != null && typeof this.props.savedGroup != 'undefined') {
            let reformattedDims = [];
            this.getDimensions().forEach(function (item) {
                reformattedDims.push({
                    dimensions: item
                })
            });
            let first = this.props.savedGroup.aggregationDimensions.map(a => a.dimensions).sort(),
                second = reformattedDims.map(a => a.dimensions).sort();
            return !(first.length === second.length && first.every((element, index) => isEqual(element, second[index])));
        }
    }

    getDimensions() {
        let dimensions = [];
        this.state.data.forEach((row) => {
            if (row.dimensions.firstAttribute != '' || row.dimensions.secondAttribute != '' || row.dimensions.thirdAttribute != '' ||
            row.dimensions.fourthAttribute != '' || row.dimensions.fifthAttribute != '') {
                let dims = [];
                if (row.dimensions.firstAttribute != '')
                    dims.push(row.dimensions.firstAttribute);

                if (row.dimensions.secondAttribute != '')
                    dims.push(row.dimensions.secondAttribute);

                if (row.dimensions.thirdAttribute != '')
                    dims.push(row.dimensions.thirdAttribute);

                if (row.dimensions.fourthAttribute != '')
                    dims.push(row.dimensions.fourthAttribute);

                if (row.dimensions.fifthAttribute != '')
                    dims.push(row.dimensions.fifthAttribute);

                dims.push(row.cumulative);
                dimensions.push(dims);
            }
        });
        return dimensions;
    }

    setTableData(params, fieldsToAggregate) {
        if (Object.keys(params).length !== 0) {
            let data = [];
            let id = 1;
            let invalidDimensionFound = false;
            params.aggregationDimensions.forEach((record, idx) => {
                let invalid = false;
                for (let rec of record.dimensions) {
                    if (rec != null && rec != '') {
                        let attribute = fieldsToAggregate.find(obj => {
                            return rec == obj.value;
                        });
                        if (typeof attribute == "undefined") {
                            invalid = true;
                            if (fieldsToAggregate.length > 0) {
                                invalidDimensionFound = true;
                            }
                        }
                    }
                }
                if (!invalid) {
                    data.push({
                        aggID: uuid(),
                        dimensions: this.constructDimensions(record.dimensions),
                        cumulative: record.cumulative
                    })
                }
            });

            if (data.length > 0) {
                data.sort((a, b) => (a['dimensions']['firstAttribute'] < b['dimensions']['firstAttribute'] ? -1 : 1));
            }

            this.setState({ data: data, records: params.records }, () => {
                if (invalidDimensionFound) {
                    this.sendDimensions();
                }
            });
        }
    }

    constructDimensions = (dimensions) => {
        return {
            firstAttribute: dimensions[0] ? dimensions[0] : '',
            secondAttribute: dimensions[1] ? dimensions[1] : '',
            thirdAttribute: dimensions[2] ? dimensions[2] : '',
            fourthAttribute: dimensions[3] ? dimensions[3]: '',
            fifthAttribute: dimensions[4] ? dimensions[4]: ''
        }
    }

    transformDimensions = (dimensions) => {
        return dimensions.map(dim => {
            let dimensionValues = Object.values(dim.dimensions);
            return {
                cumulative: dim.cumulative,
                dimensions: dimensionValues.filter(dimVal => dimVal !== '')
            }
        });
    }

    render() {
        const { classes } = this.props,
            { data, order, orderBy, selected, editMode, records, fieldsToAggregate } = this.state;

        return (
            <div className={classes.root}>
                <div className="row buttonGroup">
                    <div className="col-lg-7">
                        <FormLabel className={classes.headers} style={{ marginRight: '12pt' }}>
                            Define Aggregation Dimensions
                        </FormLabel>
                        <FormLabel style={{ fontSize: '10pt' }}>
                            Select Up To 5 Attributes/Fields for each Type of Aggregation
                        </FormLabel>
                    </div>
                    <div className="float-right">
                        <Button variant="outlined" color="secondary" className={classes.button} onClick={this.handleAddRow}
                            disabled={editMode}>
                            Add
                        </Button>
                        <Button variant="outlined" color="secondary" className={classes.button}
                            disabled={this.state.selected.length < 1} onClick={this.handleDeleteRow}>
                            Delete
                        </Button>
                    </div>
                </div>
                <div>

                </div>
                <br />
                <Paper>
                    {/*<EnhancedTableToolbar numSelected={selected.length}/>*/}
                    <div className={classes.tableWrapper}>
                        <Table className={classes.table} aria-labelledby="tableTitle">
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={this.handleSelectAllClick}
                                onSelectAllCulumative={this.handleSelectAllCumulative}
                                onRequestSort={this.handleRequestSort}
                                rowCount={data.length}
                                editMode={editMode}
                                records={records}
                                classes={classes}
                                data={data}
                            />
                            <TableBody>
                                {data.map(dimension => {
                                    const isSelected = this.isSelected(dimension.aggID);
                                    return (
                                        <TableRow
                                            hover={records == null || records == 0}
                                            aria-checked={isSelected}
                                            tabIndex={-1}
                                            key={dimension.aggID}
                                            selected={isSelected}
                                        >
                                            <TableCell style={{ padding: 1 }}>
                                                <Checkbox onClick={event => this.handleClick(event, dimension.aggID)}
                                                    checked={isSelected}
                                                    disabled={editMode} />
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15 }}>
                                                <select
                                                    id={dimension.aggID}
                                                    onChange={this.handleFieldChange}
                                                    value={dimension.dimensions.firstAttribute}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                    name='firstAttribute'
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15 }}>
                                                <select
                                                    id={dimension.aggID}
                                                    onChange={this.handleFieldChange}
                                                    value={dimension.dimensions.secondAttribute}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                    name='secondAttribute'
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15 }}>
                                                <select
                                                    id={dimension.aggID}
                                                    onChange={this.handleFieldChange}
                                                    value={dimension.dimensions.thirdAttribute}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                    name='thirdAttribute'
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15 }}>
                                                <select
                                                    id={dimension.aggID}
                                                    onChange={this.handleFieldChange}
                                                    value={dimension.dimensions.fourthAttribute}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                    name='fourthAttribute'
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15 }}>
                                                <select
                                                    id={dimension.aggID}
                                                    onChange={this.handleFieldChange}
                                                    value={dimension.dimensions.fifthAttribute}
                                                    style={{ border: '1px solid gray', width: "100%", borderRadius: 5, fontSize: 17 }}
                                                    disabled={editMode}
                                                    name='fifthAttribute'
                                                >
                                                    <option key={0} value={''}>{''}</option>
                                                    {fieldsToAggregate.map((e, key) => {
                                                        return <option key={e.value} value={e.value}>{e.name}</option>;
                                                    })}
                                                </select>
                                            </TableCell>
                                            <TableCell style={{ width: '25%', padding: 10, fontSize: 15, textAlign: 'center' }}>
                                                <Checkbox
                                                    onClick={this.handleCulumativeChange}
                                                    name='cumulative'
                                                    id={dimension.aggID}
                                                    checked={dimension.cumulative}
                                                    disabled={editMode} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

            </div>
        );
    }
}

DimensionsTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DimensionsTable);



// WEBPACK FOOTER //
// ./src/main/content/define-data-group/DimensionsTable.js